'use client';

/**
 * The Vale toast entry point (LF-6246).
 *
 * App code imports `toast` from HERE, never from `sonner` directly — a lint rule
 * enforces it (`@typescript-eslint/no-restricted-imports` in `eslint.config.mjs`).
 * The `<Toaster>` that renders these lives in `@/components/ui/sonner` and is
 * mounted once in the root layout.
 *
 * The wrapper is deliberately a thin pass-through: every call signature and every
 * option is sonner's, unchanged, so a call site needs no edit beyond its import.
 * It owns two things a caller may not switch off — **dismissal**, and, for the
 * methods a caller uses to END a loading toast, clearing what `loading()` left on
 * the same id:
 *
 *   - `dismissible: false` is stripped. Sonner honours it by killing swipe-to-dismiss
 *     AND turning the close button into a no-op click handler, which ships a visible
 *     X that does nothing.
 *   - `loading()` renders as a default-type toast carrying the Vale mark's pulsing
 *     glyph (VALE-1251) rather than
 *     sonner's `loading` type, because sonner hard-codes `toastType !== 'loading'`
 *     into its close-button render gate — a real `loading` toast can never show an X.
 *     Everything else about it is unchanged: it still returns an id, still swaps in
 *     place when a later `toast.success(msg, { id })` reuses that id, and still stays
 *     open until something closes it (sonner exempts its own `loading` type from the
 *     auto-close timer; the wrapper re-applies that by defaulting `duration`).
 *   - `success()`/`error()`/`info()`/`warning()`/`message()`/the bare call re-declare
 *     `icon` and `duration` (see `endsLoading` below) so that swap-in-place at the
 *     same id doesn't inherit the pulsing glyph or the Infinity duration `loading()`
 *     set — sonner's same-id update is a shallow merge onto the old toast, so a key
 *     absent from the new call's options survives from the old one untouched
 *     (VALE-482).
 *   - `custom()` gets a close button injected over the caller's JSX, because sonner
 *     also gates the close button on `!toast.jsx`.
 *
 * It owns one consequence of that, too. Because a toast now has exits its author may
 * not have anticipated, a `cancel` action is re-homed: its handler runs on ALL of
 * them — the X, a swipe and the "C" shortcut — and **no cancel button is rendered**,
 * since a "Dismiss" CTA only duplicated exits the toast already has while eating
 * into the message's wrap width. See `cancelBecomesDismissCleanup` below for why
 * that can't double-fire.
 *
 * The one gap, stated plainly: `promise()`'s in-flight phase is created inside sonner
 * as a real `loading` toast and cannot be redirected from out here, so it shows no X.
 * It is still swipe-dismissible, and the global "C" shortcut clears it like anything
 * else — `toast.dismiss()` with no id ignores both `dismissible` and toast type.
 */

import type { MouseEvent } from 'react';
import { X } from 'lucide-react';
import { ValeAIMark } from '@/components/icons/vale-ai-mark';
import { toast as sonnerToast, type ExternalToast } from 'sonner';

type SonnerToast = typeof sonnerToast;

/**
 * Turn a `cancel` action into dismissal cleanup, and render no button for it.
 *
 * A `cancel` action means "the user is done with this toast" — callers hang real
 * cleanup off it (cancelling a timer, clearing a ref), not just a close. Two things
 * follow from that, and this owns both:
 *
 *   - **The cleanup runs on EVERY exit.** Sonner only ever calls `cancel.onClick`
 *     from the button it renders, which was sound while that button was the toast's
 *     only exit. It no longer is: the X, a swipe and the "C" shortcut all close the
 *     same toast, and on those paths the cleanup was silently skipped — leaving, in
 *     the AI-analysis case, a stall timer live enough to resurrect the toast the
 *     user had just dismissed.
 *   - **No cancel button is rendered.** `cancel` is deleted before sonner sees it,
 *     so sonner's `[data-cancel]` button never renders. Every toast already carries
 *     an X and the "C" shortcut, so a "Dismiss" CTA was a third control for
 *     something the toast could already do — and it competed with the message for
 *     the row, narrowing the text's wrap width for no new capability. The handler
 *     was the part that mattered, not the button.
 *
 * With the button gone, `onDismiss` is the only path in, so `runOnce` now guards
 * exactly one thing: re-entry. A cancel handler that dismisses its own toast — the
 * common shape, and what the AI-analysis one does — comes straight back through
 * `onDismiss`. The guard is per toast-creation call, so re-firing a toast at the
 * same `id` correctly re-arms it.
 *
 * Deliberately NOT wired to `onAutoClose`: a toast timing out on its own is not the
 * user dismissing it.
 */
function cancelBecomesDismissCleanup(options?: ExternalToast): ExternalToast {
  const cancel = options?.cancel;
  // A ReactNode `cancel` is the caller's own markup, with its own wiring and no
  // `onClick` to lift out — left exactly as passed rather than silently dropped.
  if (!cancel || typeof cancel !== 'object' || !('onClick' in cancel)) {
    return { ...options };
  }

  let handled = false;
  const runOnce = () => {
    if (handled) return;
    handled = true;
    // None of the surviving paths — the X, a swipe, "C" — has a click event to
    // hand over. Every cancel handler in the app ignores the argument; sonner's
    // type demands one.
    (cancel.onClick as (event?: MouseEvent<HTMLButtonElement>) => void)();
  };

  const forwarded: ExternalToast = {
    ...options,
    onDismiss: (dismissed) => {
      runOnce();
      options?.onDismiss?.(dismissed);
    }
  };
  // What actually suppresses the button: sonner renders `[data-cancel]` whenever
  // it receives a cancel at all.
  delete forwarded.cancel;
  return forwarded;
}

/**
 * Force the dismissal affordances on, whatever the caller passed. Spreading the
 * caller's options FIRST is what makes this non-overridable.
 */
function alwaysClosable(options?: ExternalToast): ExternalToast {
  return {
    ...cancelBecomesDismissCleanup(options),
    dismissible: true,
    closeButton: true
  };
}

/**
 * Clear whatever `loading()` set, for the methods a caller uses to end one.
 *
 * Sonner's store updates a toast at an existing id with a shallow merge —
 * `{ ...oldToast, ...newCallOptions }` (`ToastState.create`) — so a field the
 * old toast set survives untouched unless this call's own options object has
 * that key. `loading()` below sets two: an explicit `icon` and
 * `duration: Infinity`. A caller ending a loading toast with
 * `toast.success(msg, { id })` (the pattern the module header describes)
 * passes neither, so both leaked through: the "finished" toast kept the
 * loading glyph and never auto-closed, because sonner's own auto-close effect
 * bails out whenever `toast.duration === Infinity`, regardless of `type`
 * (VALE-482). Re-declaring both here — even to `undefined` — always
 * overrides them: `undefined` falls through to sonner's own type-driven icon
 * and default duration, and a caller's own value still wins.
 */
function endsLoading(options?: ExternalToast): ExternalToast {
  return {
    ...alwaysClosable(options),
    icon: options?.icon,
    duration: options?.duration
  };
}

const base = (...[message, options]: Parameters<SonnerToast>) =>
  sonnerToast(message, endsLoading(options));

const success: SonnerToast['success'] = (message, options) =>
  sonnerToast.success(message, endsLoading(options));

const error: SonnerToast['error'] = (message, options) =>
  sonnerToast.error(message, endsLoading(options));

const info: SonnerToast['info'] = (message, options) =>
  sonnerToast.info(message, endsLoading(options));

const warning: SonnerToast['warning'] = (message, options) =>
  sonnerToast.warning(message, endsLoading(options));

const message: SonnerToast['message'] = (msg, options) =>
  sonnerToast.message(msg, endsLoading(options));

/**
 * A closable loading toast. See the module header for why this is not
 * `sonnerToast.loading`.
 *
 * `duration` has to be restored by hand here. Sonner exempts a real `loading` toast
 * from the auto-close timer outright — its effect returns early on
 * `toast.type === 'loading'` — so re-typing the toast to give it an X also dropped it
 * into the standard 4s lifetime, and a progress indicator closed itself while its work
 * was still in flight (`Uploading contract.pdf…` vanishing mid-upload, leaving the
 * user with nothing to look at until the outcome landed). `?? Infinity` keeps the
 * four call sites that pass an explicit duration in charge of their own.
 *
 * This is safe to pin open only because every `toast.loading` call site terminates —
 * each one either dismisses the id (in both the try and the catch) or replaces it
 * with a `success`/`error` at the same id. And the X and "C" are guaranteed on this
 * toast, so even a future leak has a user-reachable exit.
 */
const loading: SonnerToast['loading'] = (msg, options) =>
  sonnerToast(msg, {
    ...alwaysClosable(options),
    duration: options?.duration ?? Infinity,
    icon: options?.icon ?? (
      <ValeAIMark box={16} thinking className="text-foreground" />
    )
  });

/**
 * A custom/JSX toast with a close button laid over the caller's own markup —
 * sonner renders `jsx` toasts unstyled and never gives them a close control.
 */
const custom: SonnerToast['custom'] = (jsx, options) =>
  sonnerToast.custom(
    (id) => (
      <div className="relative">
        {jsx(id)}
        <button
          type="button"
          aria-label="Close notification"
          onClick={() => sonnerToast.dismiss(id)}
          className="border-border bg-background text-foreground hover:bg-muted absolute top-0 left-full flex size-5 -translate-x-[65%] -translate-y-[35%] items-center justify-center rounded-full border transition-colors"
        >
          <X className="size-3" />
        </button>
      </div>
    ),
    alwaysClosable(options)
  );

/**
 * Pass-through. `dismiss()` with no id clears every toast regardless of type or
 * `dismissible` — it is the backstop the "C" shortcut uses.
 */
const dismiss: SonnerToast['dismiss'] = (id) => sonnerToast.dismiss(id);

const promise: SonnerToast['promise'] = (p, data) =>
  sonnerToast.promise(p, data ? { ...data, dismissible: true } : data);

export const toast: SonnerToast = Object.assign(base, {
  success,
  error,
  info,
  warning,
  message,
  loading,
  custom,
  dismiss,
  promise,
  getHistory: sonnerToast.getHistory,
  getToasts: sonnerToast.getToasts
});

export type { ExternalToast };
